/* ── overlays ───────────────────────────────────────────────── */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * A modal, rendered into <body> rather than where it is written.
 *
 * THE PORTAL IS THE WHOLE POINT, and it fixes a real bug. Nearly every modal
 * in this app is written inside a <Card>, and cards lift on hover:
 *
 *     .card{transition:transform .25s …}
 *     .card:hover{transform:translateY(-4px)}
 *
 * A transformed ancestor becomes the containing block for `position: fixed`
 * descendants — so a modal nested in a card stops being fixed to the viewport
 * and is positioned against the CARD instead. Moving the pointer in and out of
 * that card's bounds, which happens constantly while reaching for the modal's
 * own fields, toggles `:hover` and slides the entire modal up and down by four
 * pixels over a quarter of a second. That is the wobble, and it hit every
 * modal written inside a card.
 *
 * Rendering into <body> puts the overlay outside any transformed ancestor, so
 * `position: fixed` means the viewport again. It also takes the overlay out of
 * the cards' stacking contexts, which is why a modal could previously be
 * overlapped by whatever came after its card.
 */
export function Modal({ open, title, onClose, children, footer, width = 640 }) {
  /* Escape closes it, as a dialog is expected to. Clicking the backdrop always
     did; the key did not, which left anyone working from the keyboard with no
     way out of a modal that traps their attention but never took their focus. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;
  return createPortal((
    <div
      className="modal-wrap open"
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ width }}>
        <div className="modal-h">
          <b>{title}</b>
          <button className="drawer-x" onClick={onClose}>✕</button>
        </div>
        <div className="modal-b">{children}</div>
        {footer && <div className="modal-f">{footer}</div>}
      </div>
    </div>
  ), document.body);
}
